import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import { Button } from 'theme-ui';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component '" + name + "' was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h2 {...{
      "id": "what-is-it",
      "style": {
        "position": "relative"
      }
    }}>{`What is it?`}</h2>
    <h3 {...{
      "id": "-headless-react-hook",
      "style": {
        "position": "relative"
      }
    }}>{`🎃 Headless React Hook`}</h3>
    <p><inlineCode parentName="p">{`useComments`}</inlineCode>{` just cares about the data. You write your own UI.`}</p>
    <h3 {...{
      "id": "-backed-with-hasura",
      "style": {
        "position": "relative"
      }
    }}>{`👺 Backed with Hasura`}</h3>
    <p>{`We need to keep all of these comments somewhere, right?
`}<a parentName="p" {...{
        "href": "https://hasura.io"
      }}>{`Hasura`}</a>{`, a GraphQL layer on top of a database, handles this
for us! Don't worry, you'll get your Hasura up and running in 30 seconds!`}</p>
    <h3 {...{
      "id": "-quick-and-easy",
      "style": {
        "position": "relative"
      }
    }}>{`🚀 Quick and easy`}</h3>
    <p>{`The setup will take less than 5 minutes.`}</p>
    <h2 {...{
      "id": "getting-started",
      "style": {
        "position": "relative"
      }
    }}>{`Getting started`}</h2>
    <p>{`Let's add comments to a website in five simple steps!`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Deploy Hasura`}</strong></p>
        <p parentName="li">{`Click this 👇 to deploy a fresh `}<a parentName="p" {...{
            "href": "https://hasura.io"
          }}>{`Hasura`}</a>{` instance.`}</p>
        <Button as="a" href="https://heroku.com/deploy?template=https://github.com/hasura/graphql-engine-heroku" variant="links.button" sx={{
          my: 2
        }} mdxType="Button">
  Deploy Hasura to Heroku
        </Button>
        <p parentName="li">{`Check out `}<a parentName="p" {...{
            "href": "https://hasura.io/docs/1.0/graphql/manual/guides/deployment/heroku-one-click.html"
          }}>{`the docs`}</a>{` for more details. You can find more options
for one-click deployment there.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Set config vars in Heroku`}</strong></p>
        <p parentName="li">{`In order to use our Hasura backend we need to set two enviromental variables
in Heroku.`}</p>
        <p parentName="li">{`Setting
`}<a parentName="p" {...{
            "href": "https://hasura.io/docs/1.0/graphql/manual/deployment/heroku/securing-graphql-endpoint.html#heroku-secure"
          }}><inlineCode parentName="a">{`HASURA_GRAPHQL_ADMIN_SECRET`}</inlineCode></a>{`
makes sure that your GraphQL endpoint and the Hasura console are not publicly
accessible.`}</p>
        <p parentName="li"><a parentName="p" {...{
            "href": "https://hasura.io/docs/1.0/graphql/manual/auth/authentication/unauthenticated-access.html#unauthenticated-access"
          }}><inlineCode parentName="a">{`HASURA_GRAPHQL_UNAUTHORIZED_ROLE`}</inlineCode></a>{`
allows unauthenticated (non-logged in) users add and view comments.`}</p>
        <img width="700" src="./heroku-env.png" />
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Import database schema and metadata`}</strong></p>
        <p parentName="li">{`In the next step we need to import database schema. Click
`}<a parentName="p" {...{
            "href": "https://github.com/beerose/use-comments/blob/master/hasura/up.sql"
          }}>{`here`}</a>{` and
copy paste the content.`}</p>
        <img width="600" src="./sql.png" />
        <p parentName="li">{`We also need to import Hasura metadata to set all the permissions. Save
`}<a parentName="p" {...{
            "href": "https://github.com/beerose/use-comments/blob/master/hasura/metadata.json"
          }}>{`this file`}</a>{`,
and import it in Hasura Console:`}</p>
        <img width="300" src="./metadata.png" />
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Install `}<inlineCode parentName="strong">{`use-comments`}</inlineCode></strong></p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`npm install use-comments
`}</code></pre>
        <pre parentName="li"><code parentName="pre" {...{}}>{`yarn add use-comments
`}</code></pre>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import { useComments, CommentStatus } from 'use-comments';

const { comments, count, loading, refetch, error } = useComments(
  hasuraUrl,
  'landing-page'
);
`}</code></pre>
        <p parentName="li">{`Or add it from CDN:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-js"
          }}>{`<script
  crossorigin
  src="https://unpkg.com/use-comments@0.1.3/dist/index.umd.js"
></script>

<script type="module" async>
   const { useComments } = useComments;

   const { comments, count, loading, refetch, error } = useComments(
     hasuraUrl,
     'landing-page',
   );
</script>
`}</code></pre>
        <p parentName="li">{`You can find your `}<inlineCode parentName="p">{`hasuraUrl`}</inlineCode>{` in the Hasura Console:`}</p>
        <img width="500" src="./url.png" />
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Create beautiful UI for your comments`}</strong></p>
        <p parentName="li">{`Start off from one of the examples or write it from scratch.`}</p>
        {
          /* <div style="display: flex; flex-wrap: wrap;">
           <img
             style="width: 300px; height: 300px; margin-right: 20px; cursor: pointer"
             src="./t.png"
           />
           <img
             style="width: 300px; height: 300px; margin-right: 20px; cursor: pointer"
             src="./tu.png"
           />
           <img
             style="width: 300px; height: 300px; margin-right: 20px; cursor: pointer"
             src="./p.png"
           />
          </div> */
        }
      </li>
    </ol>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://codesandbox.io/s/use-comments-theme-ui-demo-hjqqj"
        }}>{`Theme UI`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://codesandbox.io/s/use-comments-demo-tailwind-pvhgw"
        }}>{`Tailwind`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://codesandbox.io/s/use-comments-pagination-z5kmg"
        }}>{`Theme UI with pagination`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      